import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { Camera, Circle, Square, RotateCcw, Send, Star, Check, Loader2, Upload, AlertCircle, Compass } from "lucide-react";
import { API } from "../lib/adventureApi";

const MAX_SECONDS = 60;
const TIPS = ["Your favourite moment", "What surprised you most", "Who would you recommend it to?"];

function TipsList() {
  return (
    <div className="rounded-xl border border-white/10 bg-white/5 p-4" data-testid="review-tips">
      <p className="font-mono text-[10px] uppercase tracking-widest text-white/50 mb-3">A few ideas to talk about</p>
      <ol className="space-y-2.5">
        {TIPS.map((t, i) => (
          <li key={t} className="flex items-center gap-3 text-sm text-white/90">
            <span className="w-6 h-6 rounded-full bg-terracotta/20 text-terracotta font-mono text-xs flex items-center justify-center shrink-0">{i + 1}</span>{t}
          </li>
        ))}
      </ol>
    </div>
  );
}

function TipTicker() {
  const [i, setI] = useState(0);
  useEffect(() => { const id = setInterval(() => setI((n) => (n + 1) % TIPS.length), 5000); return () => clearInterval(id); }, []);
  return (
    <div className="absolute bottom-4 inset-x-4 flex justify-center pointer-events-none" data-testid="review-tip-ticker">
      <p key={i} className="animate-in fade-in slide-in-from-bottom-2 duration-500 bg-black/60 backdrop-blur px-4 py-2 rounded-full text-sm text-white text-center">
        <span className="text-terracotta font-mono text-xs mr-2">{i + 1}/{TIPS.length}</span>{TIPS[i]}
      </p>
    </div>
  );
}
const IS_IOS = /iPad|iPhone|iPod/.test(navigator.userAgent) || (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
const IN_APP = IS_IOS && (/FBAN|FBAV|FB_IAB|Instagram|Messenger|WhatsApp|Line\/|Snapchat|LinkedInApp|Twitter|TikTok|musical_ly/i.test(navigator.userAgent) || !/Safari\//.test(navigator.userAgent));

function SafariButton() {
  const [copied, setCopied] = useState(false);
  const href = window.location.href.replace(/^https?:\/\//, "x-safari-https://");
  const copy = () => navigator.clipboard?.writeText(window.location.href).then(() => setCopied(true)).catch(() => {});
  return (
    <div className="flex flex-col gap-2 w-full" data-testid="review-safari-box">
      <a href={href} data-testid="review-open-safari"
        className="h-14 rounded-full bg-terracotta text-white font-semibold text-base flex items-center justify-center gap-2 active:scale-[0.98] transition-transform">
        <Compass className="w-5 h-5" /> Open in Safari to record
      </a>
      <button onClick={copy} data-testid="review-copy-link" className="text-xs text-white/60 underline">
        {copied ? "Link copied — paste it into Safari" : "Button not working? Copy the link and paste it into Safari"}
      </button>
    </div>
  );
}

const pickMime = () => {
  if (typeof MediaRecorder === "undefined") return null;
  return ["video/mp4", "video/webm;codecs=vp8,opus", "video/webm"].find((t) => MediaRecorder.isTypeSupported(t)) || "";
};

function Shell({ children }) {
  return (
    <div className="min-h-[100dvh] bg-ink text-white flex flex-col" data-testid="video-review-page">
      <header className="px-5 pt-5 pb-3 flex items-center gap-2">
        <span className="w-2 h-2 rounded-full bg-terracotta" />
        <span className="font-mono text-[11px] uppercase tracking-[0.3em] text-white/60">Responsible Adventures</span>
      </header>
      <main className="flex-1 flex flex-col px-5 pb-6">{children}</main>
    </div>
  );
}

function Stars({ value, onChange }) {
  return (
    <div className="flex gap-2" data-testid="review-stars">
      {[1, 2, 3, 4, 5].map((n) => (
        <button key={n} type="button" onClick={() => onChange(n)} data-testid={`review-star-${n}`}
          className="p-1 active:scale-90 transition-transform" aria-label={`${n} stars`}>
          <Star className={`w-9 h-9 ${n <= value ? "fill-terracotta text-terracotta" : "text-white/30"}`} />
        </button>
      ))}
    </div>
  );
}

function Timer({ seconds }) {
  const left = MAX_SECONDS - seconds;
  return (
    <div className="absolute top-4 left-1/2 -translate-x-1/2 flex items-center gap-2 bg-black/60 backdrop-blur px-3 py-1.5 rounded-full" data-testid="review-timer">
      <span className="w-2.5 h-2.5 rounded-full bg-red-500 animate-pulse" />
      <span className="font-mono text-sm tabular-nums">0:{String(left).padStart(2, "0")}</span>
    </div>
  );
}

function Details({ invite, blob, onBack, onSent }) {
  const [name, setName] = useState((invite.client_name || "").split(" ")[0]);
  const [rating, setRating] = useState(5);
  const [consent, setConsent] = useState(false);
  const [progress, setProgress] = useState(null);
  const [err, setErr] = useState("");
  const { token } = useParams();

  const send = () => {
    setErr("");
    if (!name.trim()) return setErr("Please add your first name");
    if (!consent) return setErr("Please tick the box so we can share your review");
    const ext = blob.type.includes("mp4") || blob.type.includes("quicktime") ? "mp4" : "webm";
    const fd = new FormData();
    fd.append("file", blob, `review.${ext}`);
    fd.append("first_name", name.trim()); fd.append("rating", String(rating)); fd.append("consent", "true");
    const xhr = new XMLHttpRequest();
    xhr.open("POST", `${API}/video-reviews/invite/${token}/submit`);
    xhr.upload.onprogress = (e) => e.lengthComputable && setProgress(Math.round((e.loaded / e.total) * 100));
    xhr.onload = () => {
      if (xhr.status < 300) return onSent();
      setProgress(null);
      try { setErr(JSON.parse(xhr.responseText).detail || "Upload failed"); } catch { setErr("Upload failed, please try again"); }
    };
    xhr.onerror = () => { setProgress(null); setErr("Connection lost — please try again"); };
    setProgress(0); xhr.send(fd);
  };

  return (
    <div className="flex-1 flex flex-col gap-6 pt-4" data-testid="review-details">
      <h1 className="font-display font-extrabold text-3xl tracking-tight">Nearly done</h1>
      <label className="block">
        <span className="font-mono text-[11px] uppercase tracking-widest text-white/50">First name</span>
        <input value={name} onChange={(e) => setName(e.target.value)} data-testid="review-first-name"
          className="mt-2 w-full h-12 px-4 rounded-md bg-white/10 border border-white/15 text-white text-base focus:outline-none focus:border-terracotta" />
      </label>
      <div>
        <span className="font-mono text-[11px] uppercase tracking-widest text-white/50">Your rating</span>
        <div className="mt-2"><Stars value={rating} onChange={setRating} /></div>
      </div>
      <label className="flex items-start gap-3 cursor-pointer" data-testid="review-consent-label">
        <input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} data-testid="review-consent"
          className="mt-1 w-5 h-5 accent-terracotta" />
        <span className="text-sm text-white/80 leading-relaxed">I agree that Responsible Adventures can publish this video and my first name on their website and social channels.</span>
      </label>
      {err && <p className="flex items-center gap-2 text-sm text-red-300" data-testid="review-error"><AlertCircle className="w-4 h-4" />{err}</p>}
      <div className="mt-auto flex flex-col gap-3">
        <button onClick={send} disabled={progress !== null} data-testid="review-send"
          className="h-14 rounded-full bg-terracotta text-white font-semibold text-base flex items-center justify-center gap-2 active:scale-[0.98] transition-transform disabled:opacity-70">
          {progress !== null ? <><Loader2 className="w-5 h-5 animate-spin" /> Sending… {progress}%</> : <><Send className="w-5 h-5" /> Send my review</>}
        </button>
        <button onClick={onBack} disabled={progress !== null} data-testid="review-back"
          className="h-12 rounded-full text-white/70 text-sm">Back to video</button>
      </div>
    </div>
  );
}

function Recorder({ onDone }) {
  const liveRef = useRef(null);
  const streamRef = useRef(null);
  const recRef = useRef(null);
  const chunksRef = useRef([]);
  const [state, setState] = useState("idle");
  const [seconds, setSeconds] = useState(0);
  const [err, setErr] = useState("");
  const [previewFailed, setPreviewFailed] = useState(false);
  const supported = !!(navigator.mediaDevices?.getUserMedia && pickMime() !== null);
  const [preferNative, setPreferNative] = useState(IN_APP || !supported);

  const attachLive = (el) => {
    liveRef.current = el;
    if (!el || !streamRef.current || el.srcObject === streamRef.current) return;
    el.muted = true; el.defaultMuted = true;
    el.setAttribute("muted", ""); el.setAttribute("playsinline", ""); el.setAttribute("webkit-playsinline", "");
    el.srcObject = streamRef.current;
    el.onloadedmetadata = () => el.play().catch(() => {});
    el.play().catch(() => {});
  };
  const stopStream = () => streamRef.current?.getTracks().forEach((t) => t.stop());
  useEffect(() => stopStream, []);

  useEffect(() => {
    if (state !== "recording") return;
    const id = setInterval(() => setSeconds((s) => {
      if (s + 1 >= MAX_SECONDS) recRef.current?.stop();
      return Math.min(s + 1, MAX_SECONDS);
    }), 1000);
    return () => clearInterval(id);
  }, [state]);

  const openCamera = async () => {
    setErr("");
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: "user", width: { ideal: 720 }, height: { ideal: 1280 } }, audio: true,
      });
      streamRef.current = stream;
      setState("ready");
    } catch {
      setErr("We couldn't access your camera. Please allow camera and microphone access, or upload a video instead.");
    }
  };

  const start = () => {
    const mime = pickMime();
    const rec = new MediaRecorder(streamRef.current, { ...(mime ? { mimeType: mime } : {}), videoBitsPerSecond: 2_500_000 });
    chunksRef.current = [];
    rec.ondataavailable = (e) => e.data.size && chunksRef.current.push(e.data);
    rec.onstop = () => {
      const blob = new Blob(chunksRef.current, { type: (rec.mimeType || mime || "video/webm").split(";")[0] });
      stopStream(); onDone(blob);
    };
    recRef.current = rec; setSeconds(0); rec.start(1000); setState("recording");
  };

  const onFile = (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    setErr("");
    const v = document.createElement("video");
    const url = URL.createObjectURL(f);
    const done = (ok) => { URL.revokeObjectURL(url); ok ? onDone(f) : setErr(`That video is a bit long — please keep it under ${MAX_SECONDS} seconds and try again.`); };
    v.preload = "metadata";
    v.onloadedmetadata = () => done(!(v.duration > MAX_SECONDS + 5));
    v.onerror = () => done(true);
    v.src = url;
  };

  useEffect(() => {
    if (state !== "ready") return;
    const t0 = liveRef.current?.currentTime || 0;
    const id = setTimeout(() => {
      const v = liveRef.current;
      if (!v || !v.videoWidth || v.paused || v.currentTime <= t0) setPreviewFailed(true);
    }, 2500);
    return () => clearTimeout(id);
  }, [state]);

  const nativeBtn = (primary) => (
    <label className={`rounded-full flex items-center justify-center gap-2 cursor-pointer active:scale-[0.98] transition-transform ${primary ? "h-14 bg-terracotta text-white font-semibold text-base" : "h-12 border border-white/20 text-white/80 text-sm"}`} data-testid={primary ? "review-native-camera" : "review-upload-label"}>
      {primary || IN_APP ? <><Camera className="w-5 h-5" /> Record with my phone's camera</> : <><Upload className="w-4 h-4" /> Or upload a video from your phone</>}
      <input type="file" accept="video/*" {...(primary || IN_APP ? { capture: "user" } : {})} className="hidden" onChange={onFile} data-testid={primary ? "review-native-input" : "review-file-input"} />
    </label>
  );

  if (state === "idle") return (
    <div className="flex-1 flex flex-col gap-4 justify-end">
      <TipsList />
      {err && <p className="text-sm text-red-300 flex gap-2" data-testid="review-camera-error"><AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />{err}</p>}
      {preferNative ? (
        <>
          {IN_APP && <SafariButton />}
          <p className="text-sm text-white/60 text-center">{IN_APP ? "Or use" : "Use"} your phone's camera — tap record, then <span className="text-white">Use Video</span> to come back here.</p>
          {nativeBtn(!IN_APP)}
          {!IN_APP && nativeBtn(false)}
        </>
      ) : (
        <>
          <button onClick={openCamera} data-testid="review-open-camera"
            className="h-14 rounded-full bg-terracotta text-white font-semibold text-base flex items-center justify-center gap-2 active:scale-[0.98] transition-transform">
            <Camera className="w-5 h-5" /> Turn on camera
          </button>
          {nativeBtn(false)}
        </>
      )}
    </div>
  );

  const switchToNative = () => { stopStream(); setState("idle"); setPreviewFailed(false); setPreferNative(true); };

  return (
    <div className="flex-1 flex flex-col gap-4">
      <div className="relative flex-1 min-h-[55dvh] rounded-xl overflow-hidden bg-black">
        <video ref={attachLive} muted playsInline autoPlay onClick={(e) => e.currentTarget.play().catch(() => {})} className="absolute inset-0 w-full h-full object-cover -scale-x-100" data-testid="review-live-video" />
        {state === "recording" && <Timer seconds={seconds} />}
        {!previewFailed && <TipTicker />}
        {previewFailed && state === "ready" && (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-4 p-6 text-center bg-ink/90" data-testid="review-preview-failed">
            <p className="text-sm text-white/80">Can't see yourself? This browser is blocking the camera preview.</p>
            {IS_IOS && <SafariButton />}
            <button onClick={switchToNative} data-testid="review-switch-native"
              className={`h-12 px-6 rounded-full font-semibold flex items-center gap-2 ${IS_IOS ? "border border-white/25 text-white" : "bg-terracotta text-white"}`}><Camera className="w-4 h-4" /> Use my phone's camera</button>
          </div>
        )}
      </div>
      <div className="flex items-center justify-center py-2">
        {state === "ready" ? (
          <button onClick={start} data-testid="review-record-start" aria-label="Start recording"
            className="w-20 h-20 rounded-full border-4 border-white flex items-center justify-center active:scale-90 transition-transform">
            <Circle className="w-14 h-14 fill-red-500 text-red-500" />
          </button>
        ) : (
          <button onClick={() => recRef.current?.stop()} data-testid="review-record-stop" aria-label="Stop recording"
            className="w-20 h-20 rounded-full border-4 border-white flex items-center justify-center active:scale-90 transition-transform">
            <Square className="w-8 h-8 fill-red-500 text-red-500" />
          </button>
        )}
      </div>
      <p className="text-center text-xs text-white/50">{state === "ready" ? `Tap to record — up to ${MAX_SECONDS} seconds` : "Tap to finish"}</p>
      {state === "ready" && (
        <button onClick={switchToNative} data-testid="review-cant-see" className="text-center text-xs text-terracotta underline">Can't see yourself? Use your phone's camera</button>
      )}
    </div>
  );
}

function Playback({ blob, onRetake, onNext }) {
  const [url] = useState(() => URL.createObjectURL(blob));
  useEffect(() => () => URL.revokeObjectURL(url), [url]);
  return (
    <div className="flex-1 flex flex-col gap-4">
      <div className="relative flex-1 min-h-[55dvh] rounded-xl overflow-hidden bg-black">
        <video src={`${url}#t=0.001`} controls playsInline preload="metadata" className="absolute inset-0 w-full h-full object-contain" data-testid="review-playback-video" />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <button onClick={onRetake} data-testid="review-retake"
          className="h-14 rounded-full border border-white/25 text-white font-semibold flex items-center justify-center gap-2"><RotateCcw className="w-4 h-4" /> Retake</button>
        <button onClick={onNext} data-testid="review-use-video"
          className="h-14 rounded-full bg-terracotta text-white font-semibold flex items-center justify-center gap-2"><Check className="w-4 h-4" /> Use this</button>
      </div>
    </div>
  );
}

export default function VideoReviewRecord() {
  const { token } = useParams();
  const [invite, setInvite] = useState(null);
  const [step, setStep] = useState("loading");
  const [blob, setBlob] = useState(null);

  useEffect(() => {
    fetch(`${API}/video-reviews/invite/${token}`).then(async (r) => {
      if (!r.ok) return setStep("invalid");
      const d = await r.json(); setInvite(d); setStep(d.submitted ? "already" : "record");
    }).catch(() => setStep("invalid"));
  }, [token]);

  if (step === "loading") return <Shell><Loader2 className="w-6 h-6 animate-spin m-auto text-terracotta" /></Shell>;
  if (step === "invalid" || step === "already" || step === "done") {
    const msg = { invalid: ["Link not found", "This review link isn't valid. Please check with the Responsible Adventures team."],
      already: ["Thank you!", "Your review has already been sent from this link."],
      done: ["Thank you!", "Your video review has been sent. We'll share it once our team has had a look."] }[step];
    return (
      <Shell>
        <div className="m-auto text-center max-w-sm" data-testid={`review-state-${step}`}>
          {step !== "invalid" && <div className="w-16 h-16 rounded-full bg-moss mx-auto mb-6 flex items-center justify-center"><Check className="w-8 h-8" /></div>}
          <h1 className="font-display font-extrabold text-3xl tracking-tight">{msg[0]}</h1>
          <p className="mt-3 text-white/70 leading-relaxed">{msg[1]}</p>
        </div>
      </Shell>
    );
  }

  return (
    <Shell>
      {step === "record" && !blob && (
        <div className="pt-2 pb-5" data-testid="review-intro">
          <h1 className="font-display font-extrabold text-3xl tracking-tight leading-tight">Hi {invite.client_name.split(" ")[0]}, how was your adventure?</h1>
          <p className="mt-3 text-white/70 text-sm leading-relaxed">
            Record a short video{invite.trip_name ? <> about <span className="text-white">{invite.trip_name}</span></> : null} — just talk to the camera, up to {MAX_SECONDS} seconds.
          </p>
        </div>
      )}
      {step === "record" && !blob && <Recorder onDone={(b) => { setBlob(b); setStep("playback"); }} />}
      {step === "playback" && <Playback blob={blob} onRetake={() => { setBlob(null); setStep("record"); }} onNext={() => setStep("details")} />}
      {step === "details" && <Details invite={invite} blob={blob} onBack={() => setStep("playback")} onSent={() => setStep("done")} />}
    </Shell>
  );
}
